import Button from 'components/base/Button';
import React, { FC, useCallback } from 'react';
import { IconType } from 'react-icons';
import { BiVideo } from 'react-icons/bi';
import {
  RiArticleLine,
  RiShoppingCart2Line,
  RiQuestionAnswerLine,
} from 'react-icons/ri';
import { MdOutlineLibraryBooks, MdOutlinePlayLesson } from 'react-icons/md';
import { IoMdImages } from 'react-icons/io';
import { GrResources } from 'react-icons/gr';
import { useAppContext } from 'provider/AppProvider/AppProvider';
import { AiFillFolder } from 'react-icons/ai';
import { FaLightbulb } from 'react-icons/fa';
import { GiBookmark } from 'react-icons/gi';

import { NavLink } from 'react-router-dom';
import CheckboxIcon from 'assets/images/checkbox.png';
import { IconProps, IFeaturedContent } from '../Hero.interface';

const Icon: FC<IconProps> = ({ type, color }) => {
  const Icons: { [key: string]: IconType | string } = {
    'Video': BiVideo,
    'Article': RiArticleLine,
    'Lesson': MdOutlineLibraryBooks,
    'Tips & Posts': FaLightbulb,
    'Audio': MdOutlinePlayLesson,
    'Q&A': RiQuestionAnswerLine,
    'Resource': GrResources,
    'Gallery': IoMdImages,
    'Product': RiShoppingCart2Line,
    'Directories': AiFillFolder,
    'Topics': RiArticleLine,
    'Courses': GiBookmark,
    'Action Lists': CheckboxIcon,
  };

  const DisplayIcon: IconType | string = Icons[type];

  if (DisplayIcon === undefined) return null;

  return typeof DisplayIcon === 'string' ? (
    <img
      className="object-scale-down w-6 h-6 invert"
      src={DisplayIcon}
      alt="Menu Icon"
    />
  ) : (
    <DisplayIcon
      className="hidden sm:block"
      size={24}
      color={color ?? 'white'}
    />
  );
};

const relativeRedirection: { [key: string]: string } = {
  'Tips & Posts': 'posts',
  'Topics': 'topics',
  'Directories': 'directories',
  'about': 'about',
};

const FeaturedContent: FC<IFeaturedContent> = ({
  target_label,
  target_url,
  target,
}) => {
  const { baseURL } = useAppContext();

  let url = target_url;

  if (target_url === '' && target !== 'Track Now') {
    url = `${String(baseURL)}/${relativeRedirection[target_label]}`;
  }

  const handleButtonIcon = (type: string) => {
    return <Icon color="white" type={type} />;
  };

  const handleRedirect = useCallback(() => {
    window.scrollTo({
      behavior: 'smooth',
      top: 0,
    });
  }, []);

  return (
    <div className="w-full sm:max-w-full x-sm:!min-w-0 md:max-w-[262px] md:w-fit md:px-0 text-center self-stretch flex justify-center items-center">
      {url ? (
        <Link className="w-full" onClick={handleRedirect} to={url}>
          <Button className="text-xl pt-4 pb-4 sm:max-w-full x-sm:!min-w-0">
            <div className="flex items-center justify-center gap-2 uppercase">
              <span className="line-clamp-2 w-full">{target_label}</span>
              {handleButtonIcon(target)}
            </div>
          </Button>
        </Link>
      ) : (
        <Button className="text-xl pt-4 pb-4 sm:max-w-full x-sm:!min-w-0">
          <div className="flex items-center justify-center gap-2 uppercase">
            <span className="line-clamp-2 w-full">{target_label}</span>
            {handleButtonIcon(target)}
          </div>
        </Button>
      )}
    </div>
  );
};
export default FeaturedContent;

interface LinkProps {
  className: string;
  to: string;
  children: React.ReactNode;
  onClick: () => void;
}

// allow props spreading since it is a wrapper.
/* eslint-disable react/jsx-props-no-spreading */

const Link: FC<LinkProps> = ({ to, children, ...props }) => {
  let pathType: 'relative' | 'absolute' = 'relative';

  if (to[0] !== '/') {
    pathType = 'absolute';
  }

  if (pathType === 'absolute') {
    const getValidUrl = (url = '') => {
      let newUrl = window.decodeURIComponent(url);
      newUrl = newUrl.trim().replace(/\s/g, '');

      if (/^(:\/\/)/.test(newUrl)) {
        return `http${newUrl}`;
      }
      if (!/^(f|ht)tps?:\/\//i.test(newUrl)) {
        return `http://${newUrl}`;
      }

      return newUrl;
    };

    return (
      <a href={getValidUrl(to)} {...props}>
        {children}
      </a>
    );
  }

  return (
    <NavLink {...props} to={to}>
      {children}
    </NavLink>
  );
};
